import { createAsyncThunk } from '@reduxjs/toolkit'
import { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import { Methods, Response } from 'types/request.ts'
import { GetSalesPartnerByIdResponse, GetSalesUserDataResponse } from 'types/sales.ts'
import { RootState } from 'store/index.ts'
import { switchLanguage } from 'store/app/thunks'
import { setLogUser } from 'utils/logging.ts'
import { RejectValue, GetSalesUserDataSuccessPayload } from './types.ts'
import { setPersistedItem } from 'utils/persistedStorage.ts'
import { LANGUAGE_STORAGE_KEY } from 'constants/constants.ts'
import { Language } from '@boltenergy-be/shared'

import { getLowerCaseLanguage } from 'utils/language.ts'

export const getSalesUserData = createAsyncThunk<GetSalesUserDataSuccessPayload, undefined, RejectValue>(
  'users/sales/getSalesUserData',
  async (_, thunkAPI) => {
    try {
      const {
        success: userSuccess,
        data: userData,
        message: userMessage
      } = await newRequest<GetSalesUserDataResponse>(getAxiosRequestConfig(Methods.GET, '/sales/users/me'))
      if (!userSuccess || !userData) {
        return thunkAPI.rejectWithValue(userMessage ?? 'error')
      }
      const { user }: GetSalesUserDataResponse = userData

      const {
        success: partnerSuccess,
        data: partnerData,
        message: partnerMessage
      }: Response<GetSalesPartnerByIdResponse> = await newRequest(getAxiosRequestConfig(Methods.GET, `/partners/${user.partnerId}`))
      if (!partnerSuccess || !partnerData?.partner) {
        return thunkAPI.rejectWithValue(partnerMessage ?? 'getPartnerByIdError')
      }
      const { partner } = partnerData

      // Persist language
      setPersistedItem(LANGUAGE_STORAGE_KEY, getLowerCaseLanguage(user.language), 365)
      const upperCaseLanguage = user.language.toUpperCase() as Language

      // Fetch current language and change it if it doesn't match the user language
      const lang = (thunkAPI.getState() as RootState).app.language
      if (lang !== upperCaseLanguage) void thunkAPI.dispatch(switchLanguage({ language: upperCaseLanguage }))

      // The sales rep is the acting user; a context would get its email redacted as customer data
      setLogUser({ id: user._id, email: user.email })

      return {
        _id: user._id,
        agentId: user.agentId,
        activated: user.activated,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        language: user.language,
        role: user.role,
        partner
      } satisfies GetSalesUserDataSuccessPayload
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)
